import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { Stack } from '@astryxdesign/core/Stack'
import { Card } from '@astryxdesign/core/Card'
import { Text } from '@astryxdesign/core/Text'
import { Button } from '@astryxdesign/core/Button'
import { IconButton } from '@astryxdesign/core/IconButton'
import { List, ListItem } from '@astryxdesign/core/List'
import { Avatar } from '@astryxdesign/core/Avatar'
import { SegmentedControl, SegmentedControlItem } from '@astryxdesign/core/SegmentedControl'
import { BottomSheet } from '@astryxdesign/core/BottomSheet'
import { catOf, useCategories, useExpenses, useShares, useTrip, useUsers } from '#/lib/hooks'
import { formatMinor, netPositions, settle } from '#/lib/money'
import { enqueue } from '#/lib/sync'
import type { Category } from '#/lib/types'

export const Route = createFileRoute('/spend')({ component: Spend })

function Spend() {
  const trip = useTrip()
  const expenses = useExpenses(trip?.id)
  const shares = useShares()
  const users = useUsers()
  const categories = useCategories(trip?.id)
  const [filter, setFilter] = useState<Category | 'all'>('all')
  // Settling up is an end-of-trip act, not something you check between meals.
  // It lives behind a button rather than on the screen you open twenty times a day.
  const [settling, setSettling] = useState(false)

  const rows = filter === 'all' ? expenses : expenses.filter((e) => e.category === filter)
  const total = expenses.reduce((s, e) => s + e.amount_minor, 0)
  const currency = trip?.base_currency ?? 'INR'

  const byCategory = useMemo(() => {
    const m = new Map<Category, number>()
    for (const e of expenses) m.set(e.category, (m.get(e.category) ?? 0) + e.amount_minor)
    return [...m.entries()].sort((a, b) => b[1] - a[1])
  }, [expenses])

  // Always derived, never stored: a late-syncing expense from another phone
  // cannot corrupt a stored balance.
  const transfers = useMemo(
    () => settle(netPositions(expenses, shares ?? [], users)),
    [expenses, shares, users],
  )

  const byDay = useMemo(() => {
    const m = new Map<string, typeof rows>()
    for (const e of rows) {
      const key = new Date(e.spent_at).toISOString().slice(0, 10)
      m.set(key, [...(m.get(key) ?? []), e])
    }
    return [...m.entries()].sort((a, b) => b[0].localeCompare(a[0]))
  }, [rows])

  const name = (id: string) => users.find((u) => u.id === id)?.display_name ?? '—'

  function exportCsv() {
    const header = 'date,amount,currency,category,paid_by,note\n'
    const body = expenses.map((e) => [
      new Date(e.spent_at).toISOString(),
      (e.amount_minor / 100).toFixed(2),
      e.currency,
      e.category,
      name(e.paid_by),
      `"${(e.note ?? '').replace(/"/g, '""')}"`,
    ].join(',')).join('\n')
    const url = URL.createObjectURL(new Blob([header + body], { type: 'text/csv' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `${trip?.name ?? 'trip'}-expenses.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Stack direction="vertical" gap={5}>
      <Stack direction="vertical" gap={0}>
        <Text type="supporting">Trip total</Text>
        <Text type="display-2" hasTabularNumbers display="block">
          {formatMinor(total, currency)}
        </Text>
      </Stack>

      {byCategory.length > 0 && (
        <Card>
          <List density="compact">
            {byCategory.map(([cat, amount]) => (
              <ListItem
                key={cat}
                label={catOf(categories, cat).label}
                startContent={<Text type="inherit">{catOf(categories, cat).icon}</Text>}
                endContent={
                  <Text hasTabularNumbers>{formatMinor(amount, currency)}</Text>
                }
              />
            ))}
          </List>
        </Card>
      )}

      <SegmentedControl
        label="Filter by category"
        value={filter}
        onChange={(v) => setFilter(v as Category | 'all')}
       
      >
        <SegmentedControlItem value="all" label="All" />
        {/* Icon only past "All": the strip scrolls, and six or more words in it
            meant the last categories were always off-screen. The label is still
            the accessible name. */}
        {categories.map((c) => (
          <SegmentedControlItem
            key={c.id}
            value={c.id}
            label={c.label}
            isLabelHidden
            icon={<Text type="inherit">{c.icon}</Text>}
          />
        ))}
      </SegmentedControl>

      {byDay.map(([day, list]) => (
        <Stack key={day} direction="vertical" gap={2}>
          <Text type="label" color="secondary">{fmtDay(day)}</Text>
          <List hasDividers density="compact">
            {list.map((e) => (
              <ListItem
                key={e.id}
                label={e.note || catOf(categories, e.category).label}
                description={`${catOf(categories, e.category).icon} ${name(e.paid_by)}`}
                startContent={
                  <Avatar name={name(e.paid_by)} />
                }
                endContent={
                  <Stack direction="horizontal" align="center" gap={2}>
                    <Text hasTabularNumbers>{formatMinor(e.amount_minor, e.currency)}</Text>
                    <IconButton
                      label="Delete"
                     
                      variant="ghost"
                      icon={<Text type="inherit">×</Text>}
                      onClick={() => enqueue({ table: 'expenses', op: 'delete', row: { id: e.id } })}
                    />
                  </Stack>
                }
              />
            ))}
          </List>
        </Stack>
      ))}

      {expenses.length === 0 && <Text type="supporting">Nothing recorded yet.</Text>}

      {expenses.length > 0 && (
        <Stack direction="vertical" gap={2}>
          <Button
            label={transfers.length ? `Settle up (${transfers.length})` : 'Settle up'}
            variant="secondary"
            width="100%"
            onClick={() => setSettling(true)}
          />
          <Button label="Export CSV" variant="ghost" width="100%" onClick={exportCsv} />
        </Stack>
      )}

      {settling && (
        <BottomSheet
          isOpen
          onOpenChange={(o) => { if (!o) setSettling(false) }}
          height="hug"
          label="Settle up"
        >
          <Stack direction="vertical" gap={3} padding={4} paddingBlockStart={6}>
            <Stack direction="vertical" gap={0}>
              <Text type="large" weight="semibold" display="block">Settle up</Text>
              <Text type="supporting">
                Derived from every expense and its shares, never stored — a late
                sync from another phone cannot corrupt it.
              </Text>
            </Stack>
            {transfers.length === 0 ? (
              <Text type="supporting">Everyone is square.</Text>
            ) : (
              <List hasDividers density="compact">
                {transfers.map((t, i) => (
                  <ListItem
                    key={i}
                    label={`${name(t.from)} → ${name(t.to)}`}
                    endContent={
                      <Text hasTabularNumbers>{formatMinor(t.amount, currency)}</Text>
                    }
                  />
                ))}
              </List>
            )}
            <Button label="Done" variant="primary" width="100%" onClick={() => setSettling(false)} />
          </Stack>
        </BottomSheet>
      )}
    </Stack>
  )
}

const fmtDay = (iso: string) => {
  const d = new Date(`${iso}T00:00:00`)
  if (iso === new Date().toISOString().slice(0, 10)) return 'Today'
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })
}
